import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IProcess } from '../../../../../common/interfaces/IProcess';
import type { IRequest } from '../../../../../common/interfaces/IRequest';
import type { IReduxState } from '../../../../configureStore';
import { RequestStatus } from '../../../../../common/enums/RequestStatus';
import { ServiceCode } from '../../../../../common/enums/ServiceCode';
import { shouldShowDrivingLicenseSleepCard } from '../../../../utils/shouldShowDrivingLicenseSleepCard';
import { PageSectionForCards } from '../../molecules/pageSection/PageSection';
import { OsasFaqCard } from '../faqCards/OsasFaqCard';
import { DrivingLicenseSleepCard } from '../osasCards/DrivingLicenseSleepCard';
import { OsasPresentationCard } from '../osasCards/OsasPresentationCard';
import { OsasSleepTipsCard } from '../osasCards/OsasSleepTipsCard';
import { DeprecatedOsasInformation } from './DeprecatedOsasInformation';

type KsyosSleepInformationProps = Readonly<{
    isOsasConsultPerformed: boolean;
}>;

export function KsyosSleepInformation(props: KsyosSleepInformationProps) {
    const { isOsasConsultPerformed } = props;
    const requestProcesses = useSelector((state: IReduxState) => state.authentication.requestProcesses);
    const processes = useSelector((state: IReduxState) => state.authentication.processes);
    const [showDrivingLicenseSleepCard, setShowDrivingLicenseSleepCard] = useState(false);
    const [showAfterConsultCards, setShowAfterConsultCards] = useState(false);

    const isOsasFlowStarted = Boolean(
        processes?.find((process: IProcess) => process.service.code === ServiceCode.OSAS)
    );

    const isOSASC2Performed = !!requestProcesses?.find((request: IRequest) =>
        [ServiceCode.OSASC2].includes(request.service.code as ServiceCode)
    );

    const somnologyRequest = requestProcesses?.find(
        (request: IRequest) =>
            request.service.code === ServiceCode.OSASO &&
            [RequestStatus.requested, RequestStatus.active, RequestStatus.accepted].includes(request.status)
    );

    useEffect(() => {
        setShowDrivingLicenseSleepCard(
            showDrivingLicenseSleepCard || shouldShowDrivingLicenseSleepCard(requestProcesses) || isOsasConsultPerformed
        );
    }, [requestProcesses, isOsasConsultPerformed]);

    useEffect(() => {
        setShowAfterConsultCards(isOSASC2Performed || isOsasConsultPerformed);
    }, [isOSASC2Performed, isOsasConsultPerformed]);

    if (!showAfterConsultCards && !showDrivingLicenseSleepCard && !isOsasFlowStarted && !somnologyRequest) {
        return null;
    }

    return (
        <PageSectionForCards
            chapterTitle="Meer informatie over slaap"
            md={12}
        >
            {showAfterConsultCards && (
                <>
                    <OsasPresentationCard />
                    <OsasSleepTipsCard />
                </>
            )}

            {showDrivingLicenseSleepCard && <DrivingLicenseSleepCard />}

            {isOsasFlowStarted && <OsasFaqCard />}

            {somnologyRequest && (
                <DeprecatedOsasInformation
                    somnologyOrganizationType={somnologyRequest.customControlData?.somnologyOrganizationType}
                />
            )}
        </PageSectionForCards>
    );
}
